import type { Doc } from '@doc';
import type { Elements } from '@graph';
import { StringId } from '@strings';
import { localeManager } from './LocaleManager.svelte';

export interface UndoState {
  doc: Doc;
  selection: Elements;
}

const UNDO_LIMIT = 50;

export class UndoManager {
  undoStack: UndoState[] = $state([]);
  redoStack: UndoState[] = $state([]);

  getUndoState?: () => UndoState;
  setUndoState?: (state: UndoState) => void;

  canUndo = $derived((this as UndoManager).undoStack.length > 0);
  canRedo = $derived((this as UndoManager).redoStack.length > 0);

  undoNote: string | undefined = $derived(
    (this as UndoManager).canUndo
      ? undefined
      : localeManager.localizedString(StringId.UNDO_DISABLED)
  );
  redoNote: string | undefined = $derived(
    (this as UndoManager).canRedo
      ? undefined
      : localeManager.localizedString(StringId.REDO_DISABLED)
  );

  saveUndoState() {
    const getUndoState = this.getUndoState;
    if (getUndoState === undefined) {
      throw new Error('getUndoState is not defined');
    }
    this.redoStack = [];
    const state = getUndoState();
    this.undoStack.push(state);
  }

  revertToLastUndoState(): UndoState | undefined {
    const getUndoState = this.getUndoState;
    if (getUndoState === undefined) {
      throw new Error('getUndoState is not defined');
    }
    const setUndoState = this.setUndoState;
    if (setUndoState === undefined) {
      throw new Error('setUndoState is not defined');
    }
    const state = this.undoStack.pop();
    if (state) {
      this.redoStack.push(getUndoState());
      setUndoState(state);
      if (this.undoStack.length > UNDO_LIMIT) {
        this.undoStack.shift();
      }
    }
    return state;
  }

  revertToLastRedoState(): UndoState | undefined {
    const getUndoState = this.getUndoState;
    if (getUndoState === undefined) {
      throw new Error('getUndoState is not defined');
    }
    const setUndoState = this.setUndoState;
    if (setUndoState === undefined) {
      throw new Error('setUndoState is not defined');
    }

    const state = this.redoStack.pop();
    if (state) {
      this.undoStack.push(getUndoState());
      setUndoState(state);
    }
    return state;
  }
}
